import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { Select } from 'factor';
import { useHistory } from 'react-router-dom';

import { AppState } from '../../../../../models/Store';
import { publisherActions, PublisherActions } from '../../../../../store/publishers/actions';
import { NestedOption, Option } from '../../../../../models/Option';
import { applicationActions, ResetError } from '../../../../../store/app/actions';
import { ErrorCreatingResponse } from '../../../../../models/Response';
import { errorFieldsMapper } from '../../../../../constants/errorFieldsMapper';
import { ExistingCampaignData } from '../../../../../models/ExistingCampaign';
import {
  selectOptionsFromString,
  convertHierarchySelectOptionsToList,
} from '../../../../../utils/helpers';

type Props = PublisherActions &
  ResetError & {
    publishers: NestedOption[];
    errorCreating: ErrorCreatingResponse | null;
    className?: string;
    selectedPublishers?: Option<number>[];
    editableCampaign: ExistingCampaignData | null;
  };

const PublishersSelectWrapperComponent = (props: Props) => {
  const {
    fetchPublishers,
    publishers,
    selectedPublishers,
    selectPublishers,
    className = '',
    errorCreating,
    resetError,
    editableCampaign,
  } = props;

  const history = useHistory();

  useEffect(() => {
    fetchPublishers();
  }, [fetchPublishers]);

  useEffect(() => {
    const { pathname } = history.location;
    if (publishers.length && pathname.indexOf('campaign-create') >= 0) {
      selectPublishers(convertHierarchySelectOptionsToList(publishers));
    }
  }, [publishers, selectPublishers, history]);

  useEffect(() => {
    if (editableCampaign && publishers.length) {
      const publishersList = convertHierarchySelectOptionsToList(publishers);
      const selectedPublishersFromCampaign = editableCampaign.publisherAdCategory
        ? selectOptionsFromString(editableCampaign.publisherAdCategory, publishersList)
        : convertHierarchySelectOptionsToList(publishers);
      selectPublishers(selectedPublishersFromCampaign);
    }
  }, [editableCampaign, publishers, selectPublishers]);

  const handleChange = (value: Option[]) => {
    selectPublishers(value);
    if (errorCreating && errorCreating.errorField === errorFieldsMapper.selectedPublishers) {
      resetError();
    }
  };

  return (
    <Select
      placeholder="Select Publisher Category"
      label="Publisher Category"
      showControlLabel
      options={publishers}
      value={selectedPublishers}
      isMulti
      isSearchable
      allSelectable
      onChange={handleChange}
      className={`_publishers ${className}`}
      multiPlaceholder={(_: any, selectedItems: any) => {
        const filteredSelections = selectedItems.filter(
          (val: { value: number }) => !publishers.find((arg) => arg.value === val.value),
        );
        const isAllSelected = publishers.every((val) =>
          selectedItems.find((arg: { value: number }) => arg.value === val.value),
        );

        return isAllSelected ? 'All Selected' : `${filteredSelections.length} Selected`;
      }}
      tooltipParams={{
        label:
          'Content categories of the websites to serve your Campaign on based on The Interactive Advertising Bureau Content Taxonomy',
      }}
    />
  );
};

const mapState = createSelector(
  (state: AppState) => state.publishers,
  (state: AppState) => state.app,
  (publishers: any, app) => ({
    publishers: publishers.publishers,
    errorCreating: app.errorCreating,
    editableCampaign: app.editableCampaign,
    selectedPublishers: publishers.selectedPublishers,
  }),
);

const mapAction = {
  fetchPublishers: publisherActions.fetchPublishers,
  selectPublishers: publisherActions.selectPublishers,
  resetError: applicationActions.resetError,
};

export const PublishersSelectWrapper = connect(
  mapState,
  mapAction,
)(PublishersSelectWrapperComponent);
